import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Container,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import FeedbackIcon from '@mui/icons-material/Feedback';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import { headerData } from '../../../data/headerCostum';

const defaultTheme = createTheme({
  palette: {
    primary: { main: '#1dc39f' },
    background: { default: '#f8fffe' },
  },
});

const STATUS_OPTIONS = ['open', 'in_progress', 'resolved', 'closed'];
const PROVIDERS = ['Telkom', 'Primahome'];

function StatusChip({ status }) {
  const map = {
    open: { color: 'warning', label: 'Open' },
    in_progress: { color: 'info', label: 'In Progress' },
    resolved: { color: 'success', label: 'Resolved' },
    closed: { color: 'default', label: 'Closed' },
  };
  const cfg = map[status] || { color: 'default', label: status };
  return <Chip size="small" color={cfg.color} label={cfg.label} variant={cfg.color === 'default' ? 'outlined' : 'filled'} />;
}

export default function AdminKeluhan() {
  const [rows, setRows] = useState([]);
  const [copyList, setCopyList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // filters
  const [searchValue, setSearchValue] = useState('');
  const [statusValue, setStatusValue] = useState('');
  const [providerValue, setProviderValue] = useState('');

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchKeluhan = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(process.env.REACT_APP_API_URL + 'api/keluhan', { headers: headerData })
      .then((res) => {
        if (!res.ok) throw new Error('Gagal mengambil data keluhan');
        return res.json();
      })
      .then((data) => {
        const list = Array.isArray(data?.rows)
          ? data.rows
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
              ? data
              : [];
        setRows(list);
        setCopyList(list);
      })
      .catch((e) => setError(e.message || 'Terjadi kesalahan'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchKeluhan();
  }, [fetchKeluhan]);

  const applyFilters = useCallback(
    (src, s, st, p) => {
      let out = src;
      if (s) {
        const q = s.toLowerCase();
        out = out.filter((r) =>
          (r.kecamatan || '').toLowerCase().includes(q) ||
          (r.kelurahan || '').toLowerCase().includes(q) ||
          (r.nama_lorong_wisata || r.nama_longwis || '').toLowerCase().includes(q) ||
          (r.alamat_lengkap || r.alamat || '').toLowerCase().includes(q) ||
          (r.nama_pic || '').toLowerCase().includes(q) ||
          (r.no_hp || '').toLowerCase().includes(q) ||
          (r.provider || '').toLowerCase().includes(q) ||
          (r.status || '').toLowerCase().includes(q)
        );
      }
      if (st) out = out.filter((r) => (r.status || '').toLowerCase() === st.toLowerCase());
      if (p) out = out.filter((r) => (r.provider || '').toLowerCase() === p.toLowerCase());
      return out;
    },
    []
  );

  useEffect(() => {
    setCopyList(applyFilters(rows, searchValue, statusValue, providerValue));
    setPage(0);
  }, [rows, searchValue, statusValue, providerValue, applyFilters]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);

  const openDetail = (row) => {
    setDetailData(row);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailData(null);
  };

  const [updating, setUpdating] = useState(null); // id being updated

  const updateStatus = async (row, newStatus) => {
    if (!row?.id || !newStatus) return;
    try {
      setUpdating(row.id);
      const res = await fetch(`${process.env.REACT_APP_API_URL}api/keluhan/${row.id}/status`, {
        method: 'PATCH',
        headers: headerData,
        body: JSON.stringify({ status: newStatus })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || 'Gagal memperbarui status');
      // update local state
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: newStatus } : r)));
    } catch (e) {
      setError(e.message || 'Gagal memperbarui status');
    } finally {
      setUpdating(null);
    }
  };

  const columns = useMemo(
    () => [
      { id: 'createdAt', label: 'Tanggal', minWidth: 140, format: (v) => v ? new Date(v).toLocaleString('id-ID') : '-' },
      { id: 'kecamatan', label: 'Kecamatan', minWidth: 120 },
      { id: 'kelurahan', label: 'Kelurahan', minWidth: 120 },
      { id: 'nama_lorong_wisata', label: 'Nama Longwis', minWidth: 160 },
      { id: 'alamat_lengkap', label: 'Alamat', minWidth: 160 },
      { id: 'nama_pic', label: 'PIC', minWidth: 120 },
      { id: 'no_hp', label: 'No. HP', minWidth: 110 },
      { id: 'provider', label: 'Provider', minWidth: 110 },
      { id: 'status', label: 'Status', minWidth: 120, format: (v) => <StatusChip status={v} /> },
    ],
    []
  );

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #e8f5e8 0%, #f0fff0 100%)', py: 4 }}>
      <ThemeProvider theme={defaultTheme}>
        <Container maxWidth="xl">
          <Paper elevation={8} sx={{ p: 4, mb: 3, borderRadius: 3, background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.8) 100%)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ width: 70, height: 70, borderRadius: '50%', backgroundColor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 3, boxShadow: 3 }}>
                <FeedbackIcon sx={{ fontSize: 35, color: 'white' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography component="h1" variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 0.5 }}>
                  Keluhan Longwis
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Pantau dan kelola keluhan dari lokasi Longwis
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Paper sx={{ p: 1.5, textAlign: 'center', minWidth: 120 }}>
                  <Typography variant="h6" color="primary.main" fontWeight={700}>{copyList.length}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Keluhan</Typography>
                </Paper>
                <Paper sx={{ p: 1.5, textAlign: 'center', minWidth: 120 }}>
                  <Typography variant="h6" color="warning.main" fontWeight={700}>{rows.filter(r => r.status === 'open').length}</Typography>
                  <Typography variant="body2" color="text.secondary">Open</Typography>
                </Paper>
                <Paper sx={{ p: 1.5, textAlign: 'center', minWidth: 120 }}>
                  <Typography variant="h6" color="success.main" fontWeight={700}>{rows.filter(r => r.status === 'resolved').length}</Typography>
                  <Typography variant="body2" color="text.secondary">Resolved</Typography>
                </Paper>
              </Box>
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Alert severity="info" sx={{ mb: 2 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={18} /> Memuat data keluhan...</Box></Alert>}
          </Paper>

          <Paper elevation={4} sx={{ p: 3, mb: 3, borderRadius: 3, background: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FilterListIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight={600}>Filter & Pencarian</Typography>
            </Box>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField fullWidth size="small" label="Pencarian" placeholder="Ketik nama longwis, kecamatan, kelurahan, PIC, atau status" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} InputProps={{ startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} /> }} />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={statusValue} label="Status" onChange={(e) => setStatusValue(e.target.value)}>
                    <MenuItem value=""><em>Semua</em></MenuItem>
                    {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Provider</InputLabel>
                  <Select value={providerValue} label="Provider" onChange={(e) => setProviderValue(e.target.value)}>
                    <MenuItem value=""><em>Semua</em></MenuItem>
                    {PROVIDERS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={1}>
                <Button variant="outlined" onClick={() => { setSearchValue(''); setStatusValue(''); setProviderValue(''); }} size="small">Reset</Button>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={8} sx={{ borderRadius: 3, overflow: 'hidden', background: 'white' }}>
            <TableContainer sx={{ maxHeight: '70vh' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 700, minWidth: 60 }}>No</TableCell>
                    {columns.map((c, i) => (
                      <TableCell key={`${c.id}-${i}`} align={c.align || 'left'} sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 700, minWidth: c.minWidth }}>{c.label}</TableCell>
                    ))}
                    <TableCell align="center" sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 700, minWidth: 200 }}>Aksi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {copyList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
                    <TableRow hover key={row.id || idx}>
                      <TableCell align="center">{idx + 1 + page * rowsPerPage}</TableCell>
                      {columns.map((c, j) => {
                        const value = row[c.id];
                        return (
                          <TableCell key={`${c.id}-${j}`}>{c.format ? c.format(value, row) : (value ?? '-')}</TableCell>
                        );
                      })}
                      <TableCell align="center">
                        <Grid container spacing={1} justifyContent="center">
                          <Grid item>
                            <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => openDetail(row)}>
                              Detail
                            </Button>
                          </Grid>
                          <Grid item>
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                              <InputLabel>Ubah Status</InputLabel>
                              <Select label="Ubah Status" value={row.status || ''} onChange={(e) => updateStatus(row, e.target.value)} disabled={updating === row.id} startAdornment={<EditIcon sx={{ mr: 1 }} fontSize="small" />}>
                                {STATUS_OPTIONS.map((s) => (
                                  <MenuItem key={s} value={s}>{s}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          {row.status === 'resolved' && (
                            <Grid item>
                              <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Selesai" />
                            </Grid>
                          )}
                        </Grid>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination rowsPerPageOptions={[10, 25, 50, 100]} component="div" count={copyList.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(e, np) => setPage(np)} onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }} />
          </Paper>

          <Dialog open={detailOpen} onClose={closeDetail} maxWidth="sm" fullWidth>
            <DialogTitle>Detail Keluhan</DialogTitle>
            <DialogContent dividers>
              {detailData ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 1.2, columnGap: 2 }}>
                  <Typography variant="body2" color="text.secondary">Tanggal</Typography>
                  <Typography variant="body2">{detailData.createdAt ? new Date(detailData.createdAt).toLocaleString('id-ID') : '-'}</Typography>

                  <Typography variant="body2" color="text.secondary">Kecamatan</Typography>
                  <Typography variant="body2">{detailData.kecamatan || '-'}</Typography>

                  <Typography variant="body2" color="text.secondary">Kelurahan</Typography>
                  <Typography variant="body2">{detailData.kelurahan || '-'}</Typography>

                  <Typography variant="body2" color="text.secondary">Nama Longwis</Typography>
                  <Typography variant="body2">{detailData.nama_lorong_wisata || detailData.nama_longwis || '-'}</Typography>

                  <Typography variant="body2" color="text.secondary">Alamat</Typography>
                  <Typography variant="body2">{detailData.alamat_lengkap || detailData.alamat || '-'}</Typography>

                  <Typography variant="body2" color="text.secondary">PIC</Typography>
                  <Typography variant="body2">{detailData.nama_pic || '-'}</Typography>

                  <Typography variant="body2" color="text.secondary">No. HP</Typography>
                  <Typography variant="body2">{detailData.no_hp || '-'}</Typography>

                  <Typography variant="body2" color="text.secondary">Provider</Typography>
                  <Typography variant="body2">{detailData.provider || '-'}</Typography>

                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Box><StatusChip status={detailData.status} /></Box>

                  <Typography variant="body2" color="text.secondary">Keluhan</Typography>
                  <Typography variant="body2">{detailData.keluhan || '-'}</Typography>
                </Box>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDetail}>Tutup</Button>
            </DialogActions>
          </Dialog>
        </Container>
      </ThemeProvider>
    </Box>
  );
}
